import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API_URL } from "../../api-config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const VolumeZScoreIntradayChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/trading-api/toolbox/volume_zscore_intraday/?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || []);
    } catch (err) {
      console.error("Error al obtener volume z-score intraday:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // refrescar cada minuto
    return () => clearInterval(interval);
  }, []);

  // Agrupar por la fecha más reciente de los datos y calcular proyecciones por checkpoint
  const chartData = useMemo(() => {
    if (!data.length) return null;

    // Agrupar registros por fecha
    const recordsByDate = {};
    data.forEach((r) => {
      if (!recordsByDate[r.fecha]) {
        recordsByDate[r.fecha] = [];
      }
      recordsByDate[r.fecha].push(r);
    });

    // Obtener fechas ordenadas descendente para identificar la más reciente
    const dates = Object.keys(recordsByDate).sort((a, b) => new Date(b) - new Date(a));
    const latestDate = dates[0];

    // Filtrar registros de hoy y ordenarlos por checkpoint (hora)
    const dailyRecords = (recordsByDate[latestDate] || [])
      .sort((a, b) => a.checkpoint.localeCompare(b.checkpoint));

    const labels = dailyRecords.map((r) => r.checkpoint);
    const linearScores = dailyRecords.map((r) => {
      const val = Number(r.zscore_linear);
      return isNaN(val) ? 0 : Number(val.toFixed(2));
    });

    // Calcular las estadísticas históricas para cada checkpoint de hoy
    const projections = dailyRecords.map((r) => {
      const c = r.checkpoint;
      const currentVol = Number(r.cum_vol) || 0;

      // Filtrar registros del mismo checkpoint en los últimos 14 días históricos
      const histRecords = data
        .filter((h) => h.checkpoint === c && h.fecha < r.fecha)
        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        .slice(0, 14);

      const n = histRecords.length;
      if (n < 2) {
        return {
          currentVol,
          hasStats: false,
        };
      }

      const vols = histRecords.map((h) => Number(h.cum_vol) || 0);
      const mu = vols.reduce((sum, v) => sum + v, 0) / n;
      const variance = vols.reduce((sum, v) => sum + Math.pow(v - mu, 2), 0) / (n - 1);
      const std = Math.sqrt(variance);

      return {
        currentVol,
        hasStats: true,
        mu,
        std,
        z2: mu + 2 * std,
        z1: mu + 1 * std,
        zn1: mu - 1 * std,
        zn2: mu - 2 * std,
      };
    });

    const formattedDate = new Date(latestDate).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC",
    });

    return {
      labels,
      dateStr: formattedDate,
      projections,
      datasets: [
        {
          label: "Z-Score Intradía",
          data: linearScores,
          backgroundColor: "rgba(59, 130, 246, 0.2)",
          borderColor: "#3b82f6",
          borderWidth: 2.5,
          pointBackgroundColor: "#3b82f6",
          pointBorderColor: "#fff",
          pointHoverRadius: 6,
          tension: 0.15,
        },
      ],
    };
  }, [data]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: "bottom",
        labels: {
          color: "#fff",
        },
      },
      tooltip: {
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 12,
        callbacks: {
          label: function (context) {
            const index = context.dataIndex;
            const datasetLabel = context.dataset.label || "";
            const zscore = context.parsed.y;
            const zscoreStr = (zscore !== null && zscore !== undefined && !isNaN(zscore)) ? zscore.toFixed(2) : "-";

            const projList = context.chart.data.projections;
            const proj = projList ? projList[index] : null;

            const lines = [];
            lines.push(`${datasetLabel}: ${zscoreStr}`);

            if (proj) {
              lines.push(`  Vol. Transado Hoy: ${proj.currentVol.toFixed(2)} M`);
              if (proj.hasStats) {
                lines.push(`  Volumen necesario a esta hora para:`);
                lines.push(`    Z =  2 (Media + 2 Desv): ${Math.max(0, proj.z2).toFixed(2)} M`);
                lines.push(`    Z =  1 (Media + 1 Desv): ${Math.max(0, proj.z1).toFixed(2)} M`);
                lines.push(`    Z =  0 (Media Histórica): ${proj.mu.toFixed(2)} M`);
                lines.push(`    Z = -1 (Media - 1 Desv): ${Math.max(0, proj.zn1).toFixed(2)} M`);
                lines.push(`    Z = -2 (Media - 2 Desv): ${Math.max(0, proj.zn2).toFixed(2)} M`);
              } else {
                lines.push("  (Datos hist. insuficientes para proyección)");
              }
            }
            return lines;
          },
        },
      },
      title: {
        display: true,
        text: `Z-Score de Volumen Intradía (${chartData ? chartData.dateStr : "-"})`,
        color: "#fff",
        font: { size: 14, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Checkpoint", color: "#fff" },
        grid: { display: false },
        ticks: { color: "#fff" },
      },
      y: {
        title: { display: true, text: "Z-Score", color: "#fff" },
        suggestedMin: -2,
        suggestedMax: 2,
        grid: {
          color: (context) => {
            if (context.tick && context.tick.value === 0) {
              return "rgba(255, 255, 255, 0.6)";
            }
            return "rgba(255, 255, 255, 0.1)";
          },
          lineWidth: (context) => {
            if (context.tick && context.tick.value === 0) {
              return 2.5;
            }
            return 1;
          },
        },
        ticks: { color: "#fff" },
      },
    },
  };

  return (
    <div className="max-w-5xl mx-auto mt-6 text-white">
      <div className="bg-gray-800 rounded-xl p-4 mb-4">
        <h2 className="text-lg font-bold text-white">Z-Score de Volumen (Media Hora)</h2>
        <p className="text-sm opacity-80 text-white">
          Visualización del Z-Score de volumen acumulado intradía y su proyección al finalizar el día.
        </p>
      </div>

      <div className="h-[320px] w-full bg-gray-900 rounded-xl p-3">
        {loading ? (
          <div className="flex h-full items-center justify-center text-white">Cargando datos...</div>
        ) : chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div className="flex h-full items-center justify-center text-white">No hay datos disponibles</div>
        )}
      </div>
    </div>
  );
};

export default VolumeZScoreIntradayChart;
